import { Flex, HStack, Text } from "@chakra-ui/react";
import { SectionBox } from "../../../components/sectionBox/SectionBox";
import { BarLineGraph } from "./barLineGraph";
import { ComponentLoading } from "../../../components/loading/ComponentLoading";
import { useQuery } from "@apollo/client";
import { GET_BRAND_SAFETY_INSIGHTS_BY_DATE } from "../../../graphqlExternal/operations/operations";
import { BreakdownTypeEnum } from "../../../graphqlExternal/generated";
import {
  returnRoundedNumber,
  unsafeRateName,
  BrandChoicesOptionsType,
} from "..";
import React from "react";
import { pageTypeOptionsType } from "../../../utils/displayGetters";

export interface DistributionByTimeProps {
  breakdownType: BreakdownTypeEnum;
  setBreakdownType: React.Dispatch<React.SetStateAction<BreakdownTypeEnum>>;
  localBrandFilter: BrandChoicesOptionsType | undefined;
  setLocalBrandFilter: React.Dispatch<
    React.SetStateAction<BrandChoicesOptionsType | undefined>
  >;
  localPageTypeFilter: pageTypeOptionsType[] | undefined;
  setLocalPageTypeFilter: React.Dispatch<
    React.SetStateAction<pageTypeOptionsType[]>
  >;
  totalUnsafeImpressions: number;
  topFlaggedCategory: string;
  totalUnsafeImpressionRate: number;
  localAdvertiserFilter: { label: string; value: string }[] | undefined;
  setLocalAdvertiserFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  startDateString: string;
  endDateString: string;
  localCampaignFilter: { label: string; value: string }[] | undefined;
  setLocalCampaignFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  localLineItemFilter: { label: string; value: string }[] | undefined;
  setLocalLineItemFilter: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[] | undefined>
  >;
  brandInsightsLoading: boolean;
}

export const DistributionByTime = (distribution: DistributionByTimeProps) => {
  const pageTypes = distribution.localPageTypeFilter?.map((page) => page.value);
  const advertiserIds = distribution.localAdvertiserFilter?.map(
    (adv) => adv.value
  );
  const campaignIds = distribution.localCampaignFilter?.map(
    (camp) => camp.value
  );
  const lineItemIds = distribution.localLineItemFilter?.map(
    (line) => line.value
  );

  const {
    data: dataByTime,
    loading: loadingByTime,
    error: errorByTime,
  } = useQuery(GET_BRAND_SAFETY_INSIGHTS_BY_DATE, {
    variables: {
      breakdownType: distribution.breakdownType,
      brand: distribution.localBrandFilter
        ? distribution.localBrandFilter.value
        : null,
      pageTypes: distribution.localPageTypeFilter ? pageTypes : null,
      advertiserIds: distribution.localAdvertiserFilter ? advertiserIds : null,
      campaignIds: distribution.localCampaignFilter ? campaignIds : null,
      adGroupIds: distribution.localLineItemFilter ? lineItemIds : null,
      startDate: distribution.startDateString,
      endDate: distribution.endDateString,
    },
    context: { clientName: "illumaDbEndpoint", type: "graphql" },
  });

  if (errorByTime) {
    console.error("Error fetching brand safety insights by date:", errorByTime);
    return <>Error </>;
  }

  return (
    <Flex pt={5} width={"100%"}>
      <SectionBox
        p={2}
        m={4}
        pl={30}
        pt={30}
        pr={8}
        width={"70%"}
        justifyContent={"center"}
      >
        <Flex justifyContent={"flex-end"} justifyItems={"center"} mr={4}></Flex>

        {loadingByTime ? (
          <ComponentLoading
            text="Loading chart..."
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="950px"
            height="100%"
          />
        ) : errorByTime || !dataByTime ? (
          <HStack
            spacing={2}
            justifyContent={"center"}
            display="flex"
            alignItems="center"
            width="1000px"
            height="100%"
          >
            <Text>{"No Data For Selected Filters"}</Text>
          </HStack>
        ) : (
          <BarLineGraph
            campaignData={
              dataByTime.brandSafetyInsightsDistributionByDate
                .insightDistributionByDate
            }
            error={errorByTime || !dataByTime ? true : false}
            breakDownType={distribution.breakdownType}
          />
        )}
      </SectionBox>
      {distribution.brandInsightsLoading ? (
        <ComponentLoading
          text="Loading metrics..."
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="300px"
          height="400px"
        />
      ) : (
        <Flex
          direction={"column"}
          width={"25%"}
          justifyContent="space-between"
          pl={30}
          gap={4}
        >
          <SectionBox
            p={2}
            m={4}
            pl={30}
            width={"100%"}
            justifyContent={"center"}
            height={"100%"}
          >
            <Flex
              direction={"column"}
              align={"center"}
              justifyContent={"center"}
              paddingTop={6}
              paddingBottom={6}
              height={"100%"}
            >
              <Text
                fontSize={"25px"}
                pl={2}
                justifyContent={"center"}
                align={"center"}
              >
                {cardNumberFormatter(distribution.totalUnsafeImpressions)}
              </Text>
              <Text
                fontSize={"15px"}
                justifyContent={"center"}
                align={"center"}
                color={"grey"}
              >
                Total Unsafe Impressions
              </Text>
            </Flex>
          </SectionBox>
          <SectionBox
            p={2}
            m={4}
            pl={30}
            width={"100%"}
            height={"100%"}
            justifyContent={"center"}
          >
            <Flex
              direction={"column"}
              align={"center"}
              justifyContent={"center"}
              paddingTop={6}
              paddingBottom={6}
              height={"100%"}
            >
              <Text
                fontSize={"25px"}
                align={"center"}
                justifyContent={"center"}
              >
                {String(distribution.topFlaggedCategory)}
              </Text>
              <Text
                fontSize={"15px"}
                justifyContent={"center"}
                align={"center"}
                color={"grey"}
              >
                {distribution.breakdownType === BreakdownTypeEnum.Keyword
                  ? "Top Flagged Unsafe Keyword"
                  : "Top Flagged Unsafe Topic"}
              </Text>
            </Flex>
          </SectionBox>
          <SectionBox p={2} m={4} pl={30} width={"100%"} height={"100%"}>
            <Flex
              direction={"column"}
              align={"center"}
              justifyContent={"center"}
              paddingTop={6}
              paddingBottom={6}
              height={"100%"}
            >
              <Text
                fontSize={"25px"}
                justifyContent={"center"}
                align={"center"}
              >
                {returnRoundedNumber(distribution.totalUnsafeImpressionRate) +
                  "%"}
              </Text>
              <Text
                fontSize={"15px"}
                justifyContent={"center"}
                align={"center"}
                color={"grey"}
              >
                {unsafeRateName(distribution.breakdownType) + " Unsafe Rate"}
              </Text>
            </Flex>
          </SectionBox>
        </Flex>
      )}
    </Flex>
  );
};

export const percentageFormatter = (num: number, dp: number) => {
  return `${(num * 100).toFixed(dp)}%`;
};

const cardNumberFormatter = (num: number) => {
  if (num === null || num === undefined) {
    return "NaN";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(2).replace(/\.00$|0$/, "") + "K";
  } else {
    return num.toLocaleString("en-US");
  }
};
